import { Box, Button, Card, CardBody, Flex, HStack, Icon, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiCheckCircle, FiEdit3, FiSlash, FiUserCheck } from "react-icons/fi";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import {
  CompetitionStageDto,
  ParticipationRequestStatus,
  PortalLifecycleStatus,
  useCreateParticipationRequestMutation,
  useCreateBidV2Mutation,
  useGetMyParticipationQuery,
  type PortalSourcingEventDetailDto,
} from "../../../../autogen/bff-api";
import { currentPathSuffix } from "../../../../common/auth/returnTo";
import { useApiError } from "../../../../common/errors/useApiError";
import { useAppSelector } from "../../../../common/redux/hooks";
import { urls } from "../../../../urls";
import { usePortalEventUrls } from "../usePortalEventUrls";

// The entire CTA surface for two-stage competitions (begrenset anbudskonkurranse): request to
// participate while requests are open, status while the buyer reviews, and the invited-to-tender
// entry point once the invitation is confirmed. Single-stage events keep the register-interest +
// start-offer pair.
export const ParticipationCta = ({ event }: { event: PortalSourcingEventDetailDto }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const portalUrls = usePortalEventUrls();
  const displayer = useApiError();
  const authState = useAppSelector((state) => state.authState.state);
  const loggedInWithOrg = authState?.type === "LoggedInWithOrgContext";
  const stage = event.competitionStage;
  const isOpen = event.lifecycle.status === PortalLifecycleStatus.Open;

  const [createRequest, { isLoading: isCreatingRequest }] = useCreateParticipationRequestMutation();
  const [createBid, { isLoading: isCreatingBid }] = useCreateBidV2Mutation();
  const { data: mine, isLoading: mineLoading } = useGetMyParticipationQuery(
    { eventId: event.id },
    { skip: !loggedInWithOrg || !stage || !isOpen },
  );

  if (!stage || !isOpen) return null;

  if (!loggedInWithOrg) {
    if (stage !== CompetitionStageDto.RequestsOpen) return null;
    return (
      <Frame
        icon={FiUserCheck}
        title={t("Want to participate in this procurement?")}
        body={t("Submit a request to participate before the deadline to compete for the contract.")}
      >
        <Button
          as={RouterLink}
          to={urls.loginWithReturnUrl(currentPathSuffix(location))}
          variant="outline"
          colorScheme="gray"
          flexShrink="0"
        >
          {t("Log in to request participation")}
        </Button>
      </Frame>
    );
  }

  if (mineLoading || !mine) return null;
  const request = mine.request ?? null;
  const status = request?.status ?? null;

  const startOrContinueRequest = async () => {
    try {
      const target =
        request && status === ParticipationRequestStatus.Draft
          ? request
          : await createRequest({ eventId: event.id }).unwrap();
      navigate(portalUrls.request.edit.go(event.id, target.id).intro);
    } catch (e) {
      displayer.trigger(e);
    }
  };

  const startOffer = async () => {
    try {
      const bid = await createBid({ eventId: event.id }).unwrap();
      navigate(portalUrls.bid.edit.go(event.id, bid.id).intro);
    } catch (e) {
      displayer.trigger(e);
    }
  };

  if (stage === CompetitionStageDto.RequestsOpen) {
    if (status === ParticipationRequestStatus.Submitted) {
      return (
        <Notice
          icon={FiCheckCircle}
          tone="success"
          title={t("Your request to participate has been submitted")}
          body={t(
            "The buyer reviews all requests after the deadline. Qualified suppliers get the invitation to tender with the bid deadline.",
          )}
        />
      );
    }
    return (
      <Frame
        icon={FiUserCheck}
        title={t("Want to participate in this procurement?")}
        body={t("Submit a request to participate before the deadline to compete for the contract.")}
      >
        <Button
          colorScheme="teal"
          leftIcon={<Icon as={FiEdit3} />}
          onClick={startOrContinueRequest}
          isLoading={isCreatingRequest}
          flexShrink={0}
        >
          {status === ParticipationRequestStatus.Draft
            ? t("Continue your request")
            : t("Request to participate")}
        </Button>
      </Frame>
    );
  }

  if (stage === CompetitionStageDto.Qualification) {
    // Verdicts are masked server-side until the invitation is confirmed, so any non-draft
    // request reads as Submitted here.
    if (status != null && status !== ParticipationRequestStatus.Draft) {
      return (
        <Notice
          icon={FiCheckCircle}
          tone="info"
          title={t("Your request is under review")}
          body={t("Qualified suppliers get the invitation to tender with the bid deadline.")}
        />
      );
    }
    return (
      <Notice
        icon={FiSlash}
        tone="muted"
        title={t("The deadline for requests to participate has passed")}
        body={t("Only suppliers who requested participation in time can be invited to tender.")}
      />
    );
  }

  // TENDERING
  if (mine.invited) {
    return (
      <Card variant="filled" bg="smPrimarySofter" rounded="xl">
        <CardBody>
          <Flex direction={{ base: "column", md: "row" }} align={{ md: "center" }} justify="space-between" gap="4">
            <Stack spacing="1">
              <Text fontWeight="semibold">{t("You are invited to tender")}</Text>
              <Text fontSize="sm" color="smMuted">
                {t("Build your offer step by step. We save your progress automatically.")}
              </Text>
            </Stack>
            <Button
              colorScheme="teal"
              leftIcon={<Icon as={FiEdit3} />}
              onClick={startOffer}
              isLoading={isCreatingBid}
              flexShrink={0}
            >
              {t("Start offer")}
            </Button>
          </Flex>
        </CardBody>
      </Card>
    );
  }
  if (request?.selectionOutcome != null && !request.selectionOutcome.selected) {
    // § 16-12 (4): qualified, but not among the selected — the announced cap applied.
    return (
      <Notice
        icon={FiSlash}
        tone="muted"
        title={t("Qualified, but not selected for tender")}
        body={
          request.selectionOutcome.reason ??
          t("Your organization was qualified, but not among the suppliers invited to tender (FOA § 16-12).")
        }
      />
    );
  }
  if (status === ParticipationRequestStatus.Rejected) {
    return (
      <Notice
        icon={FiSlash}
        tone="muted"
        title={t("Your request to participate was not qualified")}
        body={request?.qualificationVerdict?.rejectionReason ?? t("The buyer did not qualify your organization for this competition.")}
      />
    );
  }
  return (
    <Notice
      icon={FiSlash}
      tone="muted"
      title={t("The tender phase is open for invited suppliers")}
      body={t("Only suppliers who were qualified in the request phase can tender in this competition.")}
    />
  );
};

const Frame = ({
  icon,
  title,
  body,
  children,
}: {
  icon: React.ElementType;
  title: string;
  body: string;
  children: React.ReactNode;
}) => (
  <Box borderWidth="1px" borderColor="smBorder" bg="smBackground" rounded="xl" px={{ base: "4", md: "6" }} py="4">
    <HStack spacing="4" justify="space-between" align="center" flexWrap="wrap" rowGap="3">
      <HStack spacing="3" align="center">
        <Icon as={icon} boxSize="5" color="smPrimary" flexShrink="0" />
        <Stack spacing="0">
          <Text fontWeight="semibold">{title}</Text>
          <Text fontSize="sm" color="smMuted">
            {body}
          </Text>
        </Stack>
      </HStack>
      {children}
    </HStack>
  </Box>
);

const Notice = ({
  icon,
  tone,
  title,
  body,
}: {
  icon: React.ElementType;
  tone: "success" | "info" | "muted";
  title: string;
  body: string;
}) => (
  <Box
    borderWidth="1px"
    borderColor={tone === "success" ? "smSuccessSoftBorder" : "smBorder"}
    bg={tone === "success" ? "smSuccessSoft" : tone === "info" ? "smPrimarySofter" : "smBackground"}
    rounded="xl"
    px={{ base: "4", md: "6" }}
    py="4"
  >
    <HStack spacing="3" align="center">
      <Icon
        as={icon}
        boxSize="5"
        color={tone === "success" ? "smSuccess" : tone === "info" ? "smPrimary" : "smMuted"}
        flexShrink="0"
      />
      <Stack spacing="0">
        <Text fontWeight="semibold">{title}</Text>
        <Text fontSize="sm" color="smMuted">
          {body}
        </Text>
      </Stack>
    </HStack>
  </Box>
);
